// [phase-catalyst-ui-v1] Earnings Catalyst panel - Trading UI, The Signal module.
//
// Read-only advisory surface. Reads
//   /api/v1/earnings-catalysts/calendar
//   /api/v1/earnings-catalysts/watchlist
//   /api/v1/earnings-catalysts/results
//   /api/v1/earnings-catalysts/reviews
// over the same-origin nginx proxy.
//
// Hard rules:
//   - No order buttons, no live controls, no fabricated tickers/EPS values.
//   - Missing fields render as "(unavailable)" - never invented.
//   - 5-minute poll; pauses when document.hidden.
//
// Marker strings required by /etc/adversyn/trading-ui-markers.json:
//   - "earnings-catalysts"
//   - "phase-catalyst-ui-v1"

import { useCallback, useEffect, useRef, useState } from "react";
import { CalendarClock, RefreshCw } from "lucide-react";
import {
  getEarningsCalendar,
  getEarningsResults,
  getEarningsWatchlist,
  type EarningsCalendarRow,
  type EarningsPayload,
  type EarningsResultRow,
  type EarningsWatchRow,
} from "../../lib/earningsCatalystClient";

const POLL_MS = 5 * 60_000;

type TabKey = "today" | "tomorrow";

function isoDay(offsetDays: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function fmtVal(v: unknown): string {
  if (v === null || v === undefined || v === "") return "(unavailable)";
  if (typeof v === "number" && !Number.isFinite(v)) return "(unavailable)";
  return String(v);
}

function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "(unavailable)";
  return `${(v * 100).toFixed(1)}%`;
}

function fmtNum(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "(unavailable)";
  return v.toFixed(digits);
}

function biasClass(bias?: string | null): string {
  const b = (bias || "").toUpperCase();
  if (b === "BULLISH") return "text-emerald-500";
  if (b === "BEARISH") return "text-rose-500";
  return "text-[var(--muted-text)]";
}

export default function EarningsCatalystPanel() {
  const [tab, setTab] = useState<TabKey>("today");
  const [calendar, setCalendar] = useState<EarningsPayload<EarningsCalendarRow> | null>(null);
  const [calendarErr, setCalendarErr] = useState<string | null>(null);
  const [calendarLoading, setCalendarLoading] = useState(true);

  const [watchlist, setWatchlist] = useState<EarningsPayload<EarningsWatchRow> | null>(null);
  const [watchlistErr, setWatchlistErr] = useState<string | null>(null);

  const [results, setResults] = useState<EarningsPayload<EarningsResultRow> | null>(null);
  const [resultsErr, setResultsErr] = useState<string | null>(null);

  const mounted = useRef(true);

  const fetchAll = useCallback(async () => {
    const day = tab === "today" ? isoDay(0) : isoDay(1);
    const [cal, wl, res] = await Promise.all([
      getEarningsCalendar(day, day, undefined, 200),
      getEarningsWatchlist("open", undefined, 200),
      getEarningsResults(undefined, undefined, undefined, 50),
    ]);
    if (!mounted.current) return;
    if (cal.ok) {
      setCalendar(cal.data);
      setCalendarErr(null);
    } else {
      setCalendarErr(cal.error?.message || "unknown");
    }
    setCalendarLoading(false);
    if (wl.ok) {
      setWatchlist(wl.data);
      setWatchlistErr(null);
    } else {
      setWatchlistErr(wl.error?.message || "unknown");
    }
    if (res.ok) {
      setResults(res.data);
      setResultsErr(null);
    } else {
      setResultsErr(res.error?.message || "unknown");
    }
  }, [tab]);

  useEffect(() => {
    mounted.current = true;
    fetchAll();
    let timer: number | null = null;
    const start = () => {
      if (timer !== null) return;
      timer = window.setInterval(fetchAll, POLL_MS);
    };
    const stop = () => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else {
        fetchAll();
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      mounted.current = false;
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [fetchAll]);

  const watchBySymbol = new Map<string, EarningsWatchRow>();
  for (const r of watchlist?.rows || []) {
    if (r.symbol) watchBySymbol.set(r.symbol, r);
  }

  const calendarRows = calendar?.rows || [];
  const resultsRows = (results?.rows || []).slice(0, 10);

  return (
    <div
      className="space-y-4 p-4 rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)]"
      data-testid="earnings-catalyst-panel"
      data-signal-flag="phase-catalyst-ui-v1"
      data-marker="earnings-catalysts"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-emerald-500" />
          <h2 className="text-lg font-black tracking-tight uppercase">Earnings Catalysts</h2>
          <span className="text-[10px] font-mono text-[var(--muted-text)]">
            phase-catalyst-ui-v1
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-[var(--card-border)] overflow-hidden">
            <button
              type="button"
              onClick={() => setTab("today")}
              data-testid="earnings-catalyst-tab-today"
              className={`px-3 py-1 text-xs font-bold uppercase ${
                tab === "today"
                  ? "bg-emerald-500/15 text-emerald-500"
                  : "text-[var(--muted-text)]"
              }`}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setTab("tomorrow")}
              data-testid="earnings-catalyst-tab-tomorrow"
              className={`px-3 py-1 text-xs font-bold uppercase ${
                tab === "tomorrow"
                  ? "bg-emerald-500/15 text-emerald-500"
                  : "text-[var(--muted-text)]"
              }`}
            >
              Tomorrow
            </button>
          </div>
          <button
            type="button"
            onClick={fetchAll}
            data-testid="earnings-catalyst-refresh"
            className="flex items-center gap-1 px-2 py-1 text-xs uppercase font-bold text-[var(--muted-text)] hover:text-emerald-500"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>

      <p className="text-xs text-[var(--muted-text)]">
        Advisory only. Estimates from upstream calendar provider; consensus values may lag.
        Missing fields render as <span className="font-mono">(unavailable)</span> - never inferred.
      </p>

      {calendarLoading && (
        <div className="text-xs text-[var(--muted-text)]" data-testid="earnings-catalyst-loading">
          Loading...
        </div>
      )}

      {!calendarLoading && calendarRows.length === 0 && (
        <div
          className="text-xs text-[var(--muted-text)] py-3"
          data-testid="earnings-catalyst-empty"
        >
          No earnings reports scheduled for {tab === "today" ? "today" : "tomorrow"} (unavailable).
        </div>
      )}

      {calendarRows.length > 0 && (
        <div className="overflow-x-auto" data-testid="earnings-catalyst-table-wrap">
          <table
            className="w-full text-xs border-collapse"
            data-testid="earnings-catalyst-table"
          >
            <thead className="text-[var(--muted-text)] uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left py-2 pr-3">Symbol</th>
                <th className="text-left py-2 pr-3">Company</th>
                <th className="text-left py-2 pr-3">Time</th>
                <th className="text-right py-2 pr-3">EPS Est</th>
                <th className="text-right py-2 pr-3">Rev Est</th>
                <th className="text-left py-2 pr-3">Bias</th>
                <th className="text-right py-2 pr-3">Conf</th>
                <th className="text-left py-2 pr-3">Watch</th>
              </tr>
            </thead>
            <tbody>
              {calendarRows.map((r, idx) => {
                const w = r.symbol ? watchBySymbol.get(r.symbol) : undefined;
                return (
                  <tr
                    key={`${r.event_id ?? idx}-${r.symbol ?? idx}`}
                    className="border-t border-[var(--card-border)]"
                    data-testid={`earnings-catalyst-row-${r.symbol ?? idx}`}
                  >
                    <td className="py-2 pr-3 font-mono font-bold">{fmtVal(r.symbol)}</td>
                    <td className="py-2 pr-3">{fmtVal(r.company_name)}</td>
                    <td className="py-2 pr-3">{fmtVal(r.report_time)}</td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {fmtNum(r.eps_estimate)}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {fmtNum(r.revenue_estimate, 0)}
                    </td>
                    <td className={`py-2 pr-3 font-bold ${biasClass(w?.bias)}`}>
                      {fmtVal(w?.bias)}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {fmtPct(w?.confidence ?? null)}
                    </td>
                    <td className="py-2 pr-3 text-[var(--muted-text)]">
                      {w ? fmtVal(w.status) : "(unavailable)"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="pt-2 border-t border-[var(--card-border)]">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[var(--muted-text)] mb-2">
          Recent Results
        </h3>
        {resultsRows.length === 0 ? (
          <div
            className="text-xs text-[var(--muted-text)]"
            data-testid="earnings-catalyst-results-empty"
          >
            No recent results (unavailable).
          </div>
        ) : (
          <table
            className="w-full text-xs border-collapse"
            data-testid="earnings-catalyst-results-table"
          >
            <thead className="text-[var(--muted-text)] uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left py-2 pr-3">Symbol</th>
                <th className="text-left py-2 pr-3">Period</th>
                <th className="text-right py-2 pr-3">EPS</th>
                <th className="text-right py-2 pr-3">EPS d%</th>
                <th className="text-right py-2 pr-3">Rev d%</th>
                <th className="text-left py-2 pr-3">Guidance</th>
              </tr>
            </thead>
            <tbody>
              {resultsRows.map((r, idx) => (
                <tr
                  key={`${r.result_id ?? idx}-${r.symbol ?? idx}`}
                  className="border-t border-[var(--card-border)]"
                >
                  <td className="py-2 pr-3 font-mono font-bold">{fmtVal(r.symbol)}</td>
                  <td className="py-2 pr-3">{fmtVal(r.fiscal_period)}</td>
                  <td className="py-2 pr-3 text-right font-mono">
                    {fmtNum(r.reported_eps)}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono">
                    {fmtPct(r.eps_surprise_pct ?? null)}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono">
                    {fmtPct(r.revenue_surprise_pct ?? null)}
                  </td>
                  <td className="py-2 pr-3">{fmtVal(r.guidance_direction)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(calendarErr || watchlistErr || resultsErr) && (
        <div
          className="text-[10px] text-amber-600 font-mono space-y-0.5"
          data-testid="earnings-catalyst-errors"
        >
          {calendarErr && <div>calendar: {calendarErr}</div>}
          {watchlistErr && <div>watchlist: {watchlistErr}</div>}
          {resultsErr && <div>results: {resultsErr}</div>}
        </div>
      )}
    </div>
  );
}
